import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { fetchPresets, addPreset, deletePreset, updatePreset, applyPresetLocally, fetchActiveTheme, saveActiveTheme } from "../redux/slice/siteConfig.slice";

const ThemeSettings = () => {
    const dispatch = useDispatch();
    const { presets, activeTheme, loading } = useSelector((state) => state.siteConfig);

    // Default colors if none selected (Classic Valletta)
    const defaultColors = {
        primary: "#304169",
        secondary: "#C6A87C",
        tertiary: "#1E293B",
        background: "#FFFFFF",
        accent: "#A48963",
        surface: "#F8FAFC",
        light: "#94A3B8"
    };

    // Local state for new preset name only (Formik handles main form)
    const [isSavingPreset, setIsSavingPreset] = useState(false);
    const [newPresetName, setNewPresetName] = useState("");
    const [newPresetColors, setNewPresetColors] = useState({});
    const [activePresetId, setActivePresetId] = useState(null);

    useEffect(() => {
        dispatch(fetchPresets())
            .unwrap()
            .then((data) => {
                if (data && Array.isArray(data)) {
                    const activePreset = data.find(
                        (p) => p.colors?.isActive === "true" || p.colors?.isActive === true
                    );
                    if (activePreset) {
                        setActivePresetId(activePreset._id);
                        if (activePreset.colors) {
                            formik.setValues(activePreset.colors);
                            dispatch(applyPresetLocally(activePreset.colors));
                        }
                    }
                }
            })
            .catch(() => { });
    }, [dispatch]);

    // Validation Schema for Colors
    const validationSchema = Yup.object({
        primary: Yup.string().matches(/^#([0-9A-F]{3}){1,2}$/i, "Invalid Hex Code").required("Required"),
        secondary: Yup.string().matches(/^#([0-9A-F]{3}){1,2}$/i, "Invalid Hex Code").required("Required"),
        tertiary: Yup.string().matches(/^#([0-9A-F]{3}){1,2}$/i, "Invalid Hex Code").required("Required"),
        background: Yup.string().matches(/^#([0-9A-F]{3}){1,2}$/i, "Invalid Hex Code").required("Required"),
        accent: Yup.string().matches(/^#([0-9A-F]{3}){1,2}$/i, "Invalid Hex Code").required("Required"),
        surface: Yup.string().matches(/^#([0-9A-F]{3}){1,2}$/i, "Invalid Hex Code").required("Required"),
        light: Yup.string().matches(/^#([0-9A-F]{3}){1,2}$/i, "Invalid Hex Code").required("Required"),
    });

    // Formik for Theme Colors
    const formik = useFormik({
        initialValues: defaultColors,
        enableReinitialize: true,
        validationSchema,
        onSubmit: (values) => {
            if (!activePresetId) {
                toast.error("Please select a preset to set as active.");
                return;
            }
            // Persist the active theme to backend, ensuring isActive is a boolean 'true'
            const activeThemeValues = { ...values, isActive: true };
            dispatch(saveActiveTheme({ id: activePresetId, ...activeThemeValues }))
                .unwrap()
                .then(() => {
                    dispatch(applyPresetLocally(activeThemeValues));
                    // Fetch presets again to update the active status in the list
                    dispatch(fetchPresets());
                })
                .catch(() => { });
        },
    });

    // Handle individual color change with live preview
    const handleColorChange = (e) => {
        const { name, value } = e.target;
        formik.setFieldValue(name, value);

        // Sync changes to New Preset Configuration if it is open
        if (isSavingPreset) {
            setNewPresetColors(prev => ({ ...prev, [name]: value }));
        }

        // Live preview only if valid hex (basic check)
        if (/^#([0-9A-F]{3}){1,2}$/i.test(value)) {
            document.documentElement.style.setProperty(`--color-${name}`, value);
        }
    };

    const DEFAULT_PRESETS = [
        {
            name: "Classic Valletta",
            colors: {
                primary: "#304169",
                secondary: "#C6A87C",
                tertiary: "#1E293B",
                background: "#FFFFFF",
                accent: "#A48963",
                surface: "#F8FAFC",
                light: "#94A3B8"
            }
        },
        {
            name: "Dark Luxury",
            colors: {
                primary: "#0F172A",
                secondary: "#C6A87C",
                tertiary: "#1E293B",
                background: "#020617",
                accent: "#A48963",
                surface: "#0F172A",
                light: "#334155"
            }
        },
        {
            name: "Emerald Prestige",
            colors: {
                primary: "#064E3B", // Deep Emerald
                secondary: "#D97706", // Amber Gold
                tertiary: "#134E4A",
                background: "#FFFFFF",
                accent: "#F59E0B",
                surface: "#F0FDF4",
                light: "#6EE7B7"
            }
        },
        {
            name: "Ruby Royal",
            colors: {
                primary: "#881337", // Deep Rose/Ruby
                secondary: "#FCD34D", // Light Gold
                tertiary: "#4C0519",
                background: "#FFF1F2",
                accent: "#F59E0B",
                surface: "#FFF1F2",
                light: "#FB7185"
            }
        },
        {
            name: "Oceanic Calm",
            colors: {
                primary: "#155E75", // Cyan/Blue
                secondary: "#67E8F9", // Light Cyan
                tertiary: "#164E63",
                background: "#ECFEFF",
                accent: "#0E7490",
                surface: "#CFFAFE",
                light: "#A5F3FC"
            }
        }
    ];

    const allPresets = (presets || []).filter(preset => preset && typeof preset === 'object');

    const applyPreset = (presetColors, presetId) => {
        // Update Formik values
        formik.setValues(presetColors);

        // Sync to New Preset Configuration if active
        if (isSavingPreset) {
            setNewPresetColors(presetColors);
        }

        setActivePresetId(presetId);

        // Apply locally for preview
        dispatch(applyPresetLocally(presetColors));
        toast.success("Preset applied! Don't forget to save.");
    };

    const handleSavePreset = () => {
        if (!newPresetName.trim()) {
            toast.error("Please enter a preset name");
            return;
        }

        // Check if updating an existing custom preset (by name)
        // Note: New backend logic just creates a new doc for addPreset.
        // If we want to support update, we need to find ID.
        const existingPreset = presets?.find(
            p => p.name.toLowerCase() === newPresetName.trim().toLowerCase()
        );

        if (existingPreset) {
            if (!window.confirm(`Preset "${newPresetName}" already exists. Update it?`)) return;

            dispatch(updatePreset({
                id: existingPreset._id,
                name: newPresetName,
                isActive: false, // Ensure stored distinctly as boolean
                ...newPresetColors
            }))
                .unwrap()
                .then(() => {
                    toast.success("Preset updated!");
                    setNewPresetName("");
                    setNewPresetColors({});
                    setIsSavingPreset(false);
                })
                .catch(err => toast.error(`Failed to update: ${err}`));
            return;
        }

        const newPresetData = {
            name: newPresetName,
            isActive: false,  // Explicit boolean
            ...newPresetColors
        };

        dispatch(addPreset(newPresetData))
            .unwrap()
            .then(() => {
                setNewPresetName("");
                setNewPresetColors({});
                setIsSavingPreset(false);
            })
            // Toast handled in slice
            .catch((err) => { }); // Error toast handled in slice
    };

    const [deleteModal, setDeleteModal] = useState({ show: false, presetId: "", presetName: "" });

    const confirmDeletePreset = (e, presetId, presetName) => {
        e.stopPropagation();
        setDeleteModal({ show: true, presetId, presetName });
    };

    const handleDeletePreset = () => {
        dispatch(deletePreset(deleteModal.presetId))
            .unwrap()
            .then(() => {
                setDeleteModal({ show: false, presetId: "", presetName: "" });
            })
            // Toats handled in slice
            .catch((err) => { });
    };

    return (
        <div className="p-6 relative">
            {/* Delete Confirmation Modal */}
            {deleteModal.show && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-fade-in backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full border border-gray-100 transform scale-100 transition-all">
                        <div className="flex flex-col items-center text-center">
                            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-600"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Preset?</h3>
                            <p className="text-sm text-gray-500 mb-6">
                                Are you sure you want to delete <span className="font-semibold text-gray-700">"{deleteModal.presetName}"</span>? This action cannot be undone.
                            </p>

                            {/* Show colors of the preset being deleted */}
                            {(() => {
                                // Find by ID for custom presets
                                const presetToDelete = presets.find(p => p._id === deleteModal.presetId);
                                if (presetToDelete && presetToDelete.colors) {
                                    return (
                                        <div className="w-full mb-6 bg-gray-50 p-4 rounded-xl border border-gray-100">
                                            <div className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3 text-left">Preset Colors</div>
                                            <div className="grid grid-cols-7 gap-1 h-12 w-full rounded-lg overflow-hidden border border-gray-200">
                                                <div className="w-full h-full" style={{ backgroundColor: presetToDelete.colors.primary }} title="Primary"></div>
                                                <div className="w-full h-full" style={{ backgroundColor: presetToDelete.colors.secondary }} title="Secondary"></div>
                                                <div className="w-full h-full" style={{ backgroundColor: presetToDelete.colors.tertiary }} title="Tertiary"></div>
                                                <div className="w-full h-full" style={{ backgroundColor: presetToDelete.colors.background }} title="Background"></div>
                                                <div className="w-full h-full" style={{ backgroundColor: presetToDelete.colors.accent }} title="Accent"></div>
                                                <div className="w-full h-full" style={{ backgroundColor: presetToDelete.colors.surface }} title="Surface"></div>
                                                <div className="w-full h-full" style={{ backgroundColor: presetToDelete.colors.light }} title="Light"></div>
                                            </div>
                                            <div className="mt-3 grid grid-cols-2 gap-2 text-left">
                                                {Object.entries(presetToDelete.colors).map(([key, value]) => (
                                                    <div key={key} className="flex items-center gap-2 text-xs text-gray-600">
                                                        <span className="w-3 h-3 rounded-full border border-gray-300 shadow-sm" style={{ backgroundColor: value }}></span>
                                                        <span className="capitalize">{key}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                }
                                return null;
                            })()}
                            <div className="flex gap-3 w-full">
                                <button
                                    onClick={() => setDeleteModal({ show: false, presetId: "", presetName: "" })}
                                    className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeletePreset}
                                    className="flex-1 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors shadow-md"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <h1 className="text-3xl font-bold mb-8 text-primary">Theme Settings</h1>

            {/* Presets Section */}
            <div className="mb-8">
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
                    {/* Add New Preset Card */}
                    <div className="group relative flex flex-col h-full col-span-1 md:col-span-2 lg:col-span-2 row-span-2">
                        {!isSavingPreset ? (
                            <button
                                onClick={() => {
                                    setIsSavingPreset(true);
                                    // Initialize preset colors from current form validation or defaults
                                    setNewPresetColors(formik.values);
                                }}
                                className="w-full h-48 rounded-2xl border-2 border-dashed border-gray-300 hover:border-secondary hover:bg-secondary/5 flex flex-col items-center justify-center transition-all duration-300 group mb-3"
                            >
                                <div className="w-12 h-12 rounded-full bg-gray-100 group-hover:bg-white flex items-center justify-center mb-2 transition-colors">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400 group-hover:text-secondary"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                                </div>
                                <span className="text-sm font-semibold text-gray-500 group-hover:text-secondary">Create Custom Preset</span>
                            </button>
                        ) : (
                            <div className="w-full h-auto rounded-2xl border border-gray-200 bg-white p-6 flex flex-col gap-4 shadow-xl mb-3 animate-fade-in relative z-10">
                                <h3 className="text-sm font-bold text-gray-800 uppercase tracking-widest text-center border-b pb-2">New Preset Configuration</h3>

                                <input
                                    type="text"
                                    placeholder="Preset Name (e.g. Sunset Vibes)"
                                    value={newPresetName}
                                    onChange={(e) => setNewPresetName(e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-md focus:border-secondary focus:ring-1 focus:ring-secondary text-sm font-semibold mb-2"
                                    autoFocus
                                />

                                <div className="grid grid-cols-2 gap-3 max-h-60 overflow-y-auto pr-1">
                                    {Object.entries(newPresetColors)
                                        .filter(([key]) => key !== 'isActive')
                                        .map(([key, value]) => (
                                            <div key={key} className="flex items-center gap-2 bg-gray-50 p-2 rounded-md border border-gray-100">
                                                <input
                                                    type="color"
                                                    value={value}
                                                    onChange={(e) => setNewPresetColors(prev => ({ ...prev, [key]: e.target.value }))}
                                                    className="w-8 h-8 rounded cursor-pointer border-none bg-transparent p-0"
                                                />
                                                <div className="flex flex-col overflow-hidden">
                                                    <span className="text-xs font-bold text-gray-500 capitalize">{key}</span>
                                                    <span className="text-[10px] text-gray-400 font-mono truncate">{value}</span>
                                                </div>
                                            </div>
                                        ))}
                                </div>

                                <div className="flex gap-3 justify-end mt-2 pt-2 border-t border-gray-100">
                                    <button
                                        onClick={() => setIsSavingPreset(false)}
                                        className="px-4 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-100 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSavePreset}
                                        className="px-6 py-2 rounded-lg bg-secondary text-white text-sm font-bold shadow-md hover:bg-opacity-90 transition-colors flex items-center gap-2"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                        Save Preset
                                    </button>
                                </div>
                            </div>
                        )}
                        <span className="opacity-0 hidden">Placeholder</span>
                    </div>
                    {allPresets.map((preset, index) => (
                        <button
                            key={preset._id || index}
                            type="button"
                            onClick={() => applyPreset(preset.colors, preset._id)}
                            className="group text-left relative"
                        >
                            <div className={`w-full aspect-square rounded-2xl overflow-hidden shadow-sm border border-gray-200 group-hover:shadow-xl group-hover:ring-2 transition-all duration-300 mb-3 flex flex-col relative group/preset ${preset.colors?.isActive === "true" || preset.colors?.isActive === true
                                ? "ring-2 ring-primary ring-offset-2"
                                : "ring-primary/20"
                                }`}>
                                {/* Delete button for custom presets (indicated by _id being present) */}
                                {preset?._id && (
                                    <div
                                        onClick={(e) => confirmDeletePreset(e, preset?._id, preset?.name)}
                                        className="absolute top-2 right-2 bg-white/80 p-1 rounded-full text-red-500 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white z-10"
                                        title="Delete Preset"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path></svg>
                                    </div>
                                )}

                                {preset?.colors && Object.entries(preset.colors).map(([key, value]) => (
                                    <div key={key} className="flex-1 w-full relative group/color" style={{ backgroundColor: value }}>
                                        {/* <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[8px] font-mono text-white/90 drop-shadow-md opacity-0 group-hover/preset:opacity-100 transition-opacity pointer-events-none uppercase">{key}</span>*/}
                                    </div>
                                ))}
                            </div>
                            <span className="text-sm font-bold text-gray-600 group-hover:text-primary pl-1 block truncate" title={preset?.name}>{preset?.name}</span>
                        </button>
                    ))}
                </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
                <h2 className="text-xl font-semibold mb-6 text-gray-800 border-b pb-2">Global Color Palette</h2>
                <form onSubmit={formik.handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {Object.keys(formik.initialValues).map((key) => (
                            <div key={key} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                <label className="text-sm font-bold text-gray-700 mb-3 capitalize flex items-center gap-2">
                                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: formik.values[key] }}></span>
                                    {key}
                                </label>
                                <div className="flex flex-col gap-3">
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="color"
                                            name={key}
                                            value={formik.values[key]}
                                            onChange={handleColorChange}
                                            className="h-12 w-12 cursor-pointer rounded-lg border border-gray-300 p-1 bg-white hover:scale-105 transition-transform"
                                        />
                                        <input
                                            type="text"
                                            name={key}
                                            value={formik.values[key]}
                                            onChange={handleColorChange}
                                            onBlur={formik.handleBlur}
                                            className={`block w-full rounded-md border shadow-sm focus:border-secondary focus:ring-secondary sm:text-sm p-2 uppercase font-mono tracking-widest bg-white ${formik.touched[key] && formik.errors[key] ? 'border-red-500 text-red-600' : 'border-gray-300 text-gray-600'
                                                }`}
                                        />
                                    </div>
                                    {formik.touched[key] && formik.errors[key] && (
                                        <div className="text-red-500 text-xs mt-1">{formik.errors[key]}</div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-10 pt-6 border-t border-gray-100 flex justify-end gap-4">
                        <button
                            type="button"
                            onClick={() => {
                                dispatch(fetchPresets()); // Reset from server
                                formik.resetForm();
                            }}
                            className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                        >
                            Reset Changes
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="bg-primary hover:bg-opacity-90 text-white py-2.5 px-8 rounded-lg transition-colors disabled:opacity-50 font-medium shadow-md hover:shadow-lg transform active:scale-95 duration-200"
                        >
                            {loading ? "Saving Palette..." : "Save Theme Configuration"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ThemeSettings;
